import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Fuse from "fuse.js";
import "../../css/Tools/EarlyCareerSearch.css";

// Import Firebase hook for data fetching
import { useFirestoreData } from "../../hooks/useFirestoreData";

import earlysearch from "../../assets/header-images/earlysearch.jpg";
import headerpic from "../../assets/header-images/headerpic.png";

// Constants for better maintainability
const ITEMS_PER_PAGE = 20;

// Sorting options
const SORT_OPTIONS = [
  { value: "relevance", label: "Relevance" },
  { value: "alphabetical", label: "Alphabetical (A–Z)" },
  { value: "deadline", label: "Deadline Soon" },
  { value: "recent", label: "Recently Added" },
];

// Fuse.js configuration for fuzzy search
const FUSE_OPTIONS = {
  keys: [
    {
      name: "name",
      weight: 0.4, // Highest weight for name matches
    },
    {
      name: "description",
      weight: 0.3, // Medium weight for description matches
    },
    {
      name: "type",
      weight: 0.15, // Lower weight for type matches
    },
    {
      name: "field",
      weight: 0.1, // Lower weight for field matches
    },
    {
      name: "race",
      weight: 0.05, // Lowest weight for race matches
    },
  ],
  threshold: 0.4, // Lower threshold = more strict matching (0.0 = exact, 1.0 = match anything)
  distance: 100, // Maximum distance for a match
  minMatchCharLength: 2, // Minimum character length for matching
  includeScore: true, // Include match score in results
  includeMatches: true, // Include match information for highlighting
  ignoreLocation: true, // Don't consider location of match in string
  findAllMatches: true, // Find all matches, not just the first one
};

// Helper function to highlight exact search term matches only
const highlightMatches = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  // Create a regex to find exact word matches (case-insensitive)
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`\\b${escapedSearchTerm}\\b`, "gi");

  // Replace exact matches with highlighted version
  return text.replace(regex, (match) => {
    return `<mark class="search-highlight">${match}</mark>`;
  });
};

// Helper functions for sorting
const parseDeadline = (deadlineStr) => {
  if (!deadlineStr || deadlineStr === "N/A") return new Date("2099-12-31"); // Far future for items without deadlines

  // Handle various deadline formats
  const cleanDeadline = deadlineStr.toLowerCase().trim();

  // Handle "rolling" deadlines
  if (cleanDeadline.includes("rolling")) return new Date("2099-01-01");

  // Handle "varies" or similar
  if (cleanDeadline.includes("varies") || cleanDeadline.includes("variable")) {
    return new Date("2099-06-01");
  }

  // Try to parse common date formats
  const datePatterns = [
    /(\w+)\s+(\d{1,2}),?\s+(\d{4})/i, // "January 15, 2024" or "January 15 2024"
    /(\d{1,2})\/(\d{1,2})\/(\d{4})/, // "01/15/2024"
    /(\d{4})-(\d{1,2})-(\d{1,2})/, // "2024-01-15"
  ];

  for (const pattern of datePatterns) {
    const match = cleanDeadline.match(pattern);
    if (match) {
      try {
        const date = new Date(deadlineStr);
        if (!isNaN(date.getTime())) return date;
      } catch (e) {
        // Continue to next pattern
      }
    }
  }

  // If no pattern matches, try direct parsing
  try {
    const date = new Date(deadlineStr);
    if (!isNaN(date.getTime())) return date;
  } catch (e) {
    // Return far future if parsing fails
  }

  return new Date("2099-12-31");
};

const sortItems = (items, sortOption, hasSearchTerm = false) => {
  const itemsToSort = [...items];

  switch (sortOption) {
    case "alphabetical":
      return itemsToSort.sort((a, b) => {
        const itemA = a.item || a;
        const itemB = b.item || b;
        return itemA.name.localeCompare(itemB.name);
      });

    case "deadline":
      return itemsToSort.sort((a, b) => {
        const itemA = a.item || a;
        const itemB = b.item || b;
        const deadlineA = parseDeadline(itemA["application-period"]);
        const deadlineB = parseDeadline(itemB["application-period"]);
        return deadlineA - deadlineB; // Earliest deadlines first
      });

    case "recent":
      return itemsToSort.sort((a, b) => {
        const itemA = a.item || a;
        const itemB = b.item || b;
        // Since we don't have actual creation dates, we'll use a proxy
        // You can replace this with actual date fields if available
        const dateA = itemA.dateAdded
          ? new Date(itemA.dateAdded)
          : new Date("2024-01-01");
        const dateB = itemB.dateAdded
          ? new Date(itemB.dateAdded)
          : new Date("2024-01-01");
        return dateB - dateA; // Most recent first
      });

    case "relevance":
    default:
      // If there's a search term, keep Fuse.js relevance order
      // Otherwise, sort alphabetically as default
      if (hasSearchTerm) {
        return itemsToSort; // Keep original Fuse.js order
      } else {
        return itemsToSort.sort((a, b) => {
          const itemA = a.item || a;
          const itemB = b.item || b;
          return itemA.name.localeCompare(itemB.name);
        });
      }
  }
};
const FILTER_OPTIONS = {
  types: [
    { value: "internship", label: "Internships" },
    { value: "apprenticeship", label: "Apprenticeships" },
    { value: "program", label: "Programs" },
    { value: "fellowship", label: "Fellowships" },
    { value: "professional-organization", label: "Professional Organizations" },
    {
      value: "professional-development-program",
      label: "Professional Development Programs",
    },
    { value: "government-program", label: "Government Programs" },
    { value: "scholarship", label: "Scholarships" },
    { value: "course", label: "Courses" },
    { value: "event", label: "Events" },
    { value: "research", label: "Research" },
    { value: "volunteer", label: "Volunteer" },
  ],
  fields: [
    { value: "stem", label: "STEM" },
    { value: "health", label: "Health" },
    { value: "business & finance", label: "Business & Finance" },
  ],
  years: [
    { value: "open-to-all", label: "Open to All" },
    { value: "highschool", label: "High School" },
    { value: "community-college", label: "Community College" },
    { value: "freshmen", label: "Freshmen" },
    { value: "sophomore", label: "Sophomore" },
    { value: "junior", label: "Junior" },
    { value: "senior", label: "Senior" },
    { value: "graduate", label: "Graduate" },
    { value: "postdoctoral", label: "Postdoctoral" },
  ],
  races: [
    { value: "latino/hispanic", label: "Latino/Hispanic" },
    { value: "black/african-american", label: "Black/African American" },
    { value: "asian/pacific-islander", label: "Asian/Pacific Islander" },
    {
      value: "native-american/indigenous",
      label: "Native American/Indigenous",
    },
  ],
};

// Custom hook for managing filters
const useFilters = () => {
  const [filters, setFilters] = useState({
    type: "",
    year: [],
    race: "",
    field: [],
  });

  const updateFilter = useCallback((name, value) => {
    if (name === "year" || name === "field") {
      setFilters((prev) => ({
        ...prev,
        [name]: prev[name].includes(value)
          ? prev[name].filter((item) => item !== value)
          : [...prev[name], value],
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  }, []);

  const removeMultiSelectFilter = useCallback((filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: prev[filterType].filter((item) => item !== value),
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({
      type: "",
      year: [],
      race: "",
      field: [],
    });
  }, []);

  return { filters, updateFilter, removeMultiSelectFilter, clearAllFilters };
};

// Filter component for better organization
const FilterSection = ({ title, children, className = "" }) => (
  <div className={`filter-section ${className}`}>
    <h3 className="filter-title">{title}</h3>
    {children}
  </div>
);

// Multi-select filter component with checkboxes
const MultiSelectFilter = ({
  title,
  options,
  selectedValues,
  onToggle,
  onRemove,
  filterType,
  isOpen,
  onToggleOpen,
}) => {
  return (
    <div className="multi-select-container">
      <button
        className="multi-select-trigger"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        aria-label={`Toggle ${title} filter options`}
      >
        <span>{title}</span>
        <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="checkbox-list">
            {options.map((option) => (
              <label key={option.value} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => onToggle(filterType, option.value)}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-label">{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {selectedValues.length > 0 && (
        <div className="selected-chips-container">
          <p className="selected-chips-label">Selected {title}:</p>
          <div className="selected-chips">
            {selectedValues.map((value) => (
              <span key={value} className="filter-chip">
                {options.find((opt) => opt.value === value)?.label || value}
                <button
                  className="chip-remove"
                  onClick={() => onRemove(filterType, value)}
                  aria-label={`Remove ${value} filter`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to determine if deadline is soon
const isDeadlineSoon = (deadlineStr) => {
  if (!deadlineStr || deadlineStr === "N/A") return false;

  const cleanDeadline = deadlineStr.toLowerCase().trim();
  if (cleanDeadline.includes("rolling") || cleanDeadline.includes("varies"))
    return false;

  try {
    const deadline = parseDeadline(deadlineStr);
    const now = new Date();
    const thirtyDaysFromNow = new Date(
      now.getTime() + 30 * 24 * 60 * 60 * 1000
    );

    return deadline <= thirtyDaysFromNow && deadline > now;
  } catch (e) {
    return false;
  }
};

// Career card component with search highlighting
const CareerCard = ({ item, searchTerm = "" }) => {
  const highlightedName = highlightMatches(item.name, searchTerm);
  const highlightedDescription = highlightMatches(item.description, searchTerm);

  // Determine which badges to show
  const badges = [];

  if (item.featured) {
    badges.push({ type: "featured", label: "Featured" });
  }

  if (item.remote) {
    badges.push({ type: "remote", label: "Remote" });
  }

  if (item.paid) {
    badges.push({ type: "paid", label: "Paid" });
  }

  if (isDeadlineSoon(item["application-period"])) {
    badges.push({ type: "deadline", label: "Deadline Soon" });
  }

  return (
    <article className="career-card">
      <div className="career-card-image-container">
        <img
          src={`/CompaniesLogo/${item.image}`}
          alt={`${item.name} logo`}
          className="career-card-image"
          loading="lazy"
        />
        {badges.length > 0 && (
          <div className="card-badges">
            {badges.map((badge, index) => (
              <span key={index} className={`badge badge-${badge.type}`}>
                {badge.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="career-card-content">
        <h3
          className="career-card-title"
          dangerouslySetInnerHTML={{ __html: highlightedName }}
        />

        <div className="career-card-details">
          <div className="career-card-field">
            <span className="field-label">Type:</span>
            <span className="field-value">
              {Array.isArray(item.type) ? item.type.join(", ") : item.type}
            </span>
          </div>

          {item.field && (
            <div className="career-card-field">
              <span className="field-label">Field:</span>
              <span className="field-value">
                {Array.isArray(item.field) ? item.field.join(", ") : item.field}
              </span>
            </div>
          )}

          <div className="career-card-field">
            <span className="field-label">Year:</span>
            <span className="field-value">
              {Array.isArray(item.year)
                ? item.year.join(", ")
                : item.year || "N/A"}
            </span>
          </div>

          {item["application-period"] && (
            <div className="career-card-field">
              <span className="field-label">Application Period:</span>
              <span className="field-value">{item["application-period"]}</span>
            </div>
          )}
        </div>

        <p
          className="career-card-description"
          dangerouslySetInnerHTML={{ __html: highlightedDescription }}
        />

        <a
          href={item.website}
          target="_blank"
          rel="noopener noreferrer"
          className="career-card-link"
          aria-label={`Learn more about ${item.name}`}
        >
          Learn More
          <span className="link-arrow">→</span>
        </a>
      </div>
    </article>
  );
};

// Main component
const EarlyCareerSearch = () => {
  // Fetch data from Firestore
  const {
    data: allData,
    loading: dataLoading,
    error: dataError,
    refetch,
  } = useFirestoreData();

  // Initialize Fuse.js instance
  const fuse = useMemo(() => new Fuse(allData, FUSE_OPTIONS), [allData]);

  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOption, setSortOption] = useState("relevance");
  const [openDropdowns, setOpenDropdowns] = useState({
    year: false,
    field: false,
  });
  const [searchResults, setSearchResults] = useState([]);
  const { filters, updateFilter, removeMultiSelectFilter, clearAllFilters } =
    useFilters();

  // Memoized filtered data for performance with Fuse.js fuzzy search
  const filteredItems = useMemo(() => {
    // Start with all data or fuzzy search results
    let currentSearchResults = [];
    const hasSearchTerm = searchTerm.trim().length > 0;

    // Apply fuzzy search if there's a search term
    if (hasSearchTerm) {
      const fuseResults = fuse.search(searchTerm.trim());
      currentSearchResults = fuseResults;
      setSearchResults(fuseResults); // Store results with match info for highlighting
    } else {
      // If no search term, create results without match info
      currentSearchResults = allData.map((item) => ({ item, matches: [] }));
      setSearchResults([]);
    }

    // Apply additional filters to the search results
    const filtered = currentSearchResults.filter(({ item }) => {
      const matchesType = filters.type
        ? typeof item.type === "string"
          ? item.type.toLowerCase().includes(filters.type.toLowerCase())
          : Array.isArray(item.type)
          ? item.type.some((type) =>
              type.toLowerCase().includes(filters.type.toLowerCase())
            )
          : false
        : true;

      const matchesYear = filters.year.length
        ? Array.isArray(item.year)
          ? item.year.some((year) =>
              filters.year.includes(
                typeof year === "string" ? year.toLowerCase() : year.toString()
              )
            )
          : filters.year.includes(
              typeof item.year === "string"
                ? item.year.toLowerCase()
                : item.year?.toString()
            )
        : true;

      const matchesRace = filters.race
        ? typeof item.race === "string" &&
          item.race.toLowerCase().includes(filters.race.toLowerCase())
        : true;

      const matchesField = filters.field.length
        ? typeof item.field === "string" &&
          filters.field.some((filterField) =>
            item.field.toLowerCase().includes(filterField.toLowerCase())
          )
        : true;

      return matchesType && matchesYear && matchesRace && matchesField;
    });

    // Apply sorting
    return sortItems(filtered, sortOption, hasSearchTerm);
  }, [allData, fuse, searchTerm, filters, sortOption]);

  // Get visible items
  const visibleFilteredItems = useMemo(
    () => filteredItems.slice(0, visibleItems),
    [filteredItems, visibleItems]
  );

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setVisibleItems(ITEMS_PER_PAGE);
  }, []);

  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      updateFilter(name, value);
      setVisibleItems(ITEMS_PER_PAGE);
    },
    [updateFilter]
  );

  const handleSortChange = useCallback((e) => {
    setSortOption(e.target.value);
    setVisibleItems(ITEMS_PER_PAGE);
  }, []);

  const loadMoreItems = useCallback(() => {
    setIsLoading(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setVisibleItems((prev) => prev + ITEMS_PER_PAGE);
      setIsLoading(false);
    }, 300);
  }, []);

  const toggleDropdown = useCallback((dropdownType) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [dropdownType]: !prev[dropdownType],
    }));
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".multi-select-container")) {
        setOpenDropdowns({ year: false, field: false });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset visible items when filters or sort change
  useEffect(() => {
    setVisibleItems(ITEMS_PER_PAGE);
  }, [filters, searchTerm, sortOption]);

  const hasActiveFilters =
    filters.type ||
    filters.year.length > 0 ||
    filters.race ||
    filters.field.length > 0;

  // Error state component
  const ErrorDisplay = ({ error, onRetry }) => (
    <div className="error-display">
      <div className="error-icon">⚠️</div>
      <h3>Unable to Load Opportunities</h3>
      <p>{error.message}</p>
      {error.details && (
        <details className="error-details">
          <summary>Technical Details</summary>
          <p>{error.details}</p>
        </details>
      )}
      <button className="retry-btn" onClick={onRetry}>
        Try Again
      </button>
    </div>
  );

  // Loading state component
  const LoadingDisplay = () => (
    <div className="loading-display">
      <div className="loading-spinner-large"></div>
      <h3>Loading Career Opportunities...</h3>
      <p>Fetching the latest opportunities from our database</p>
    </div>
  );

  // Show error state
  if (dataError) {
    return (
      <div className="early-career-search">
        <header className="search-header">
          <div className="header-image-container">
            <img
              src={earlysearch}
              alt="Early Career Search"
              className="header-image"
            />
            <div className="header-overlay">
              <div className="header-content">
                <h1 className="header-title">Early Career Search</h1>
                <p className="header-subtitle">
                  Discover opportunities to launch your career journey
                </p>
                <div className="header-actions">
                  <Link
                    to="/tools/submit-opportunity"
                    className="submit-opportunity-btn"
                  >
                    <span className="btn-icon">➕</span>
                    Submit New Opportunity
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="search-main">
          <div className="search-container">
            <ErrorDisplay error={dataError} onRetry={refetch} />
          </div>
        </main>
      </div>
    );
  }

  // Show loading state
  if (dataLoading) {
    return (
      <div className="early-career-search">
        <header className="search-header">
          <div className="header-image-container">
            <img
              src={earlysearch}
              alt="Early Career Search"
              className="header-image"
            />
            <div className="header-overlay">
              <div className="header-content">
                <h1 className="header-title">Early Career Search</h1>
                <p className="header-subtitle">
                  Discover opportunities to launch your career journey
                </p>
                <div className="header-actions">
                  <Link
                    to="/tools/submit-opportunity"
                    className="submit-opportunity-btn"
                  >
                    <span className="btn-icon">➕</span>
                    Submit New Opportunity
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="search-main">
          <div className="search-container">
            <LoadingDisplay />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="early-career-search">
      {/* Header Section */}
      <header className="search-header">
        <div className="header-image-container">
          <img
            src={earlysearch}
            alt="Early Career Search"
            className="header-image"
          />
          <div className="header-overlay">
            <div className="header-content">
              <h1 className="header-title">Early Career Search</h1>
              <p className="header-subtitle">
                Discover opportunities to launch your career journey
              </p>
              <div className="header-actions">
                <Link
                  to="/tools/submit-opportunity"
                  className="submit-opportunity-btn"
                >
                  <span className="btn-icon">➕</span>
                  Submit New Opportunity
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="search-main">
        <div className="search-container">
          {/* Sidebar Filters */}
          <aside className="search-sidebar">
            <div className="sidebar-header">
              <h2 className="sidebar-title">Filters</h2>
              {hasActiveFilters && (
                <button
                  className="clear-filters-btn"
                  onClick={clearAllFilters}
                  aria-label="Clear all filters"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Search Input */}
            <FilterSection title="Search">
              <div className="search-input-container">
                <input
                  type="text"
                  placeholder="Search opportunities..."
                  className="search-input"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label="Search opportunities"
                />
                <span className="search-icon">🔍</span>
              </div>
            </FilterSection>

            {/* Type Filter */}
            <FilterSection title="Type">
              <select
                name="type"
                className="filter-select"
                value={filters.type}
                onChange={handleFilterChange}
                aria-label="Filter by type"
              >
                <option value="">All Types</option>
                {FILTER_OPTIONS.types.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterSection>

            {/* Field Filter */}
            <FilterSection title="Field">
              <MultiSelectFilter
                title="Select Fields"
                options={FILTER_OPTIONS.fields}
                selectedValues={filters.field}
                onToggle={updateFilter}
                onRemove={removeMultiSelectFilter}
                filterType="field"
                isOpen={openDropdowns.field}
                onToggleOpen={() => toggleDropdown("field")}
              />
            </FilterSection>

            {/* Year Filter */}
            <FilterSection title="Academic Year">
              <MultiSelectFilter
                title="Select Years"
                options={FILTER_OPTIONS.years}
                selectedValues={filters.year}
                onToggle={updateFilter}
                onRemove={removeMultiSelectFilter}
                filterType="year"
                isOpen={openDropdowns.year}
                onToggleOpen={() => toggleDropdown("year")}
              />
            </FilterSection>

            {/* Race Filter */}
            <FilterSection title="Diversity & Inclusion">
              <select
                name="race"
                className="filter-select"
                value={filters.race}
                onChange={handleFilterChange}
                aria-label="Filter by diversity programs"
              >
                <option value="">All Programs</option>
                {FILTER_OPTIONS.races.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FilterSection>
          </aside>

          {/* Results Section */}
          <section className="search-results">
            <div className="results-header">
              <div className="results-header-top">
                <h2 className="results-title">
                  {filteredItems.length} Opportunities Found
                </h2>
                <div className="sort-container">
                  <label htmlFor="sort-select" className="sort-label">
                    Sort by:
                  </label>
                  <select
                    id="sort-select"
                    className="sort-select"
                    value={sortOption}
                    onChange={handleSortChange}
                    aria-label="Sort opportunities"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {searchTerm && (
                <div className="search-status">
                  <p className="search-query">
                    <span className="search-icon-small">🔍</span>
                    Fuzzy search results for: <strong>"{searchTerm}"</strong>
                  </p>
                  <p className="search-hint">
                    Results are ranked by relevance and include partial matches
                    {searchResults.length > 0 && (
                      <span className="match-count">
                        • {searchResults.length} total matches found
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Cards Grid */}
            <div className="career-cards-grid">
              {visibleFilteredItems.map((result, index) => {
                const item = result.item || result; // Handle both search results and regular items
                return (
                  <CareerCard
                    key={`${item.name}-${index}`}
                    item={item}
                    searchTerm={searchTerm}
                  />
                );
              })}
            </div>

            {/* No Results */}
            {filteredItems.length === 0 && (
              <div className="no-results">
                <div className="no-results-icon">🔍</div>
                <h3>No opportunities found</h3>
                <p>Try adjusting your search terms or filters</p>
                {hasActiveFilters && (
                  <button
                    className="clear-filters-btn secondary"
                    onClick={clearAllFilters}
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            )}

            {/* Load More Button */}
            {filteredItems.length > visibleItems && (
              <div className="load-more-container">
                <button
                  className={`load-more-btn ${isLoading ? "loading" : ""}`}
                  onClick={loadMoreItems}
                  disabled={isLoading}
                  aria-label="Load more opportunities"
                >
                  {isLoading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Loading...
                    </>
                  ) : (
                    `Show More (${
                      filteredItems.length - visibleItems
                    } remaining)`
                  )}
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default EarlyCareerSearch;
